import { useEffect, useRef, useState } from "react";

interface VideoPlayerProps {
  cover: string;
  title: string;
  duration: string;
}

type PlayerStatus = "idle" | "loading" | "error";

export default function VideoPlayer({ cover, title, duration }: VideoPlayerProps) {
  const [status, setStatus] = useState<PlayerStatus>("idle");
  const timer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, []);

  const startPlay = () => {
    if (status === "loading") return;
    setStatus("loading");
    timer.current = window.setTimeout(() => setStatus("error"), 1500);
  };

  const retry = () => {
    setStatus("loading");
    timer.current = window.setTimeout(() => setStatus("error"), 1500);
  };

  return (
    <div className="w-full overflow-hidden rounded-lg border border-background-200 bg-foreground-950">
      <div className="relative aspect-video w-full">
        <img
          src={cover}
          alt={`${title} 视频封面`}
          title={`${title} - 黑料视频`}
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-foreground-950/55"></div>

        {status === "idle" ? (
          <button
            type="button"
            onClick={startPlay}
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 cursor-pointer"
            aria-label="播放视频（演示）"
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform hover:scale-105">
              <i className="ri-play-fill text-3xl"></i>
            </span>
            <span className="text-sm text-background-200">点击播放（演示）</span>
          </button>
        ) : null}

        {status === "loading" ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <span className="h-10 w-10 animate-spin rounded-full border-2 border-background-50/30 border-t-background-50"></span>
            <span className="text-sm text-background-200">视频加载中…</span>
          </div>
        ) : null}

        {status === "error" ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-50/10 text-accent-400">
              <i className="ri-error-warning-line text-2xl"></i>
            </span>
            <p className="text-sm font-medium text-background-50">视频源加载失败</p>
            <p className="max-w-md text-xs text-background-300">
              本站播放器为前端效果演示，暂未接入真实视频流，因此无法真正播放。
            </p>
            <button
              type="button"
              onClick={retry}
              className="mt-1 flex items-center gap-2 rounded-md border border-background-50/30 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-background-50/10 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              重新加载
            </button>
          </div>
        ) : null}

        <span className="absolute bottom-3 right-3 rounded-md bg-foreground-950/70 px-2 py-0.5 text-xs font-medium text-background-50">
          {duration}
        </span>
      </div>

      {/* Fake control bar */}
      <div className="flex items-center gap-4 bg-foreground-950 px-4 py-3">
        <i className="ri-play-circle-line text-xl text-background-300"></i>
        <div className="h-1 flex-1 rounded-full bg-background-50/20">
          <div className="h-1 w-1/4 rounded-full bg-primary-500"></div>
        </div>
        <span className="text-xs text-background-400">演示播放器</span>
        <i className="ri-fullscreen-line text-lg text-background-300"></i>
      </div>
    </div>
  );
}